'use client';

import { Button, Toast, useToast } from '@zillow/constellation-10';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRecentlyContactedListing } from '@/src/lib/hooks/query/useRecentlyContactedListing';
import { useCurrentUser } from '@/src/lib/hooks/query';
// import { ListingCard } from '@/src/components/ListingCard';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { getContactFormClickstreamContext } from '@/src/components/features/property/lib/analytics';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { emitTrackModalContactFormViewEvent } from '@/src/analytics/hotpads/events';
import { OtpSignInForm } from '@/src/components/features/auth/OtpSignIn/OtpSignInForm';
import { ContactForm } from './ContactForm';
import { useHdpContactSubmission } from './useHdpContactSubmission';
import { getActivePostContactFlows, postContactFlowShowsSuccessConfirmation } from './postContactFlowRegistry';
import type {
  ContactClickstreamContext,
  ContactFormHostMode,
  ContactFormInputValues,
  ContactFormMode,
  ContactFormStep,
  ContactSubmissionContext,
} from './types';
import { spokeContainer, spokeContent, successBody, successContainer, successTitle } from './contactFormSpoke.styles';

type ContactFormSpokeProps = {
  onClose: () => void;
  /** Overlay hosts keep the spoke mounted with open={false}; per-open state resets on close. */
  open?: boolean;
  listing?: PropertyDetailListing | null;
  mode?: ContactFormMode;
  /** Forwarded to ContactForm so multiple instances on the page (modal + inline) don't collide on element ids. */
  idPrefix?: string;
  /** "modal" (default) or "inline" — drives the clickstream `triggerObject` for submissions. */
  formMode?: ContactFormHostMode;
  clickstreamContext?: ContactClickstreamContext;
  onSubmitSuccess?: (context: ContactSubmissionContext) => void;
  /** Lets a host that owns chrome (the modal header) retitle itself as the step changes. */
  onStepChange?: (step: ContactFormStep) => void;
};

/**
 * ContactFormSpoke — inner surface for the contact flow, shared by the modal overlay and
 * the inline module.
 *
 * Runs the App Router HDP contact pipeline: collect the inquiry, sign an unauthenticated
 * renter in over email OTP, submit through v3, then hand post-contact modal context back to
 * the host. The sign-in step swaps in here rather than opening a second modal, so one
 * implementation covers both hosts.
 */
export const ContactFormSpoke = ({
  onClose,
  open,
  listing,
  mode = 'contact',
  idPrefix,
  formMode = 'modal',
  clickstreamContext,
  onSubmitSuccess,
  onStepChange,
}: ContactFormSpokeProps) => {
  const t = useTranslations('contactForm');
  const { data: user } = useCurrentUser();
  const { enqueueToast } = useToast();
  const recentlyContacted = useRecentlyContactedListing(listing, {
    loggedIn: user?.loggedIn === true,
    email: user?.data?.email,
  });
  const [acknowledgedAlias, setAcknowledgedAlias] = useState<string | null>(null);
  useEffect(() => {
    if (open === false) setAcknowledgedAlias(null);
  }, [open]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [step, setStep] = useState<ContactFormStep>('form');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpSendLimitReached, setOtpSendLimitReached] = useState(false);
  const {
    cancelPendingInquiry,
    inquiryPrefill,
    isLoading,
    requiresSignIn,
    resumeAfterSignIn,
    serverErrors,
    startOtpSignIn,
    submitInquiry,
  } = useHdpContactSubmission({
    listing,
    mode,
    formMode,
    clickstreamContext,
  });
  const { triggerLocation: modalImpressionTriggerLocation, triggerObject: modalImpressionTriggerObject } =
    getContactFormClickstreamContext(clickstreamContext, 'impression', formMode);

  useEffect(() => {
    if (formMode === 'inline') {
      return;
    }

    emitTrackModalContactFormViewEvent({
      listing,
      triggerLocation: modalImpressionTriggerLocation,
      triggerObject: modalImpressionTriggerObject,
    });
  }, [formMode, listing, modalImpressionTriggerLocation, modalImpressionTriggerObject]);

  const goToStep = (nextStep: ContactFormStep) => {
    setStep(nextStep);
    onStepChange?.(nextStep);
  };

  const finishSubmission = (context: ContactSubmissionContext) => {
    if (onSubmitSuccess) {
      const activeFlows = getActivePostContactFlows(context);
      const hasVisibleSuccessConfirmation = activeFlows.some(postContactFlowShowsSuccessConfirmation);
      if (context.mode === 'contact' && !hasVisibleSuccessConfirmation) {
        enqueueToast(
          <Toast tone="success" role="status">
            {t('successToast')}
          </Toast>,
        );
      }

      onSubmitSuccess(context);
    } else {
      setIsSuccess(true);
    }
  };

  const handleSubmit = async (values: ContactFormInputValues) => {
    // Signing in is a precondition of sending, not a branch of it.
    if (requiresSignIn) {
      const otpSignIn = await startOtpSignIn(values);
      if (!otpSignIn) return;

      setOtpEmail(otpSignIn.email);
      setOtpSendLimitReached(otpSignIn.sendLimitReached);
      goToStep('otp');
      return;
    }

    const context = await submitInquiry(values);
    if (context) {
      finishSubmission(context);
    }
  };

  /**
   * The step is an auth gate, nothing more. Once the session is live the renter is put back
   * in front of their own filled-in form and the send runs from there — so the inquiry's
   * progress and any rejection appear on the form that raised it, not on a code screen that
   * has nothing left to do with it.
   */
  const handleVerified = () => {
    goToStep('form');
    void resumeAfterSignIn().then((context) => {
      if (context) {
        finishSubmission(context);
      }
      return null;
    });
  };

  const handleBack = () => {
    cancelPendingInquiry();
    goToStep('form');
  };

  if (isSuccess) {
    const successTitleText =
      mode === 'tour'
        ? t('successTitleTour')
        : isWaitlistedListing(listing)
          ? t('successTitleWaitlist')
          : t('successTitleContact');

    return (
      <div className={spokeContainer}>
        <div className={successContainer} role="status" aria-live="polite">
          <h3 className={successTitle}>{successTitleText}</h3>
          <p className={successBody}>{t('successBody')}</p>
          <Button tone="brand" emphasis="filled" onClick={onClose}>
            {t('done')}
          </Button>
        </div>
      </div>
    );
  }

  if (
    mode === 'contact' &&
    !isWaitlistedListing(listing) &&
    recentlyContacted &&
    acknowledgedAlias !== listing?.aliasEncoded
  ) {
    return (
      <div className={spokeContainer}>
        <div className={spokeContent}>
          <p>{t('alreadyContacted')}</p>
          <Button tone="brand" emphasis="filled" onClick={() => setAcknowledgedAlias(listing?.aliasEncoded ?? null)}>
            {t('contactAgain')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={spokeContainer}>
      {/* Hidden rather than unmounted: ContactForm owns its values, and beds/baths/move-in
          date have nowhere else to live while the sign-in step is up. */}
      <div className={spokeContent} hidden={step === 'otp'}>
        {/* {listing && <ListingCard listing={listing} appearance="horizontal" readOnly />} */}
        <ContactForm
          listing={listing ?? null}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          serverErrors={serverErrors}
          inquiryPrefill={inquiryPrefill ?? undefined}
          user={
            user
              ? {
                  loggedIn: user.loggedIn,
                  info: {
                    firstName: user.data?.firstName,
                    lastName: user.data?.lastName,
                    email: user.data?.email,
                  },
                }
              : undefined
          }
          mode={mode}
          idPrefix={idPrefix}
        />
      </div>

      {step === 'otp' && (
        <div className={spokeContent}>
          <OtpSignInForm
            email={otpEmail}
            sendLimitReached={otpSendLimitReached}
            // The modal host renders the title in its own header; the inline module has no
            // chrome to borrow, so the step carries it there.
            title={formMode === 'inline' ? t('signInTitle') : undefined}
            onVerified={handleVerified}
            onBack={handleBack}
          />
        </div>
      )}
    </div>
  );
};
